import { AspectRatio } from '@src/types.ts/video';
import { useEffect, useMemo, useRef, useState } from 'react';

export interface RendererRectOffsets {
  CONTAINER_LEFT_OFFSET?: number;
  CONTAINER_TOP_OFFSET?: number;
  CONTAINER_HEIGHT_OFFSET?: number;
  CONTAINER_WIDTH_OFFSET?: number;
  OVERLAY_TOP_OFFSET?: number;
  OVERLAY_LEFT_OFFSET?: number;
}

export interface RendererRectOptions {
  /** Aspect ratio used to compute the dim container. Defaults to 16:9. */
  dimContainerAspectRatio?: number;
  /**
   * When true and the parent already matches `dimContainerAspectRatio`,
   * use a 1.0 scale factor instead of 0.8. Default: true.
   */
  dynamicScaleFactor?: boolean;
  offsets?: RendererRectOffsets;
}

export interface RendererRectState {
  containerLeft: number;
  containerTop: number;
  containerWidth: number;
  containerHeight: number;
  overlayLeft: number;
  overlayTop: number;
  overlayWidth: number;
  overlayHeight: number;
  parentWidth: number;
  parentHeight: number;
}

const ZERO_RECT: RendererRectState = {
  containerLeft: 0,
  containerTop: 0,
  containerWidth: 0,
  containerHeight: 0,
  overlayLeft: 0,
  overlayTop: 0,
  overlayWidth: 0,
  overlayHeight: 0,
  parentWidth: 0,
  parentHeight: 0,
};

/**
 * Compute the largest target-aspect-ratio rect inside a parent element.
 * Two rects are returned:
 *  - **container**: the dim-container rect, sized to `dimContainerAspectRatio`
 *    inside the parent (16:9 by default).
 *  - **overlay**: the target-aspect-ratio rect (`aspectRatio`) centered inside
 *    the container.
 *
 * When `aspectRatio === dimContainerAspectRatio`, the overlay rect equals the
 * container rect.
 *
 * Recomputes on parent resize via `ResizeObserver` and on prop changes.
 */
export function useRendererRect(
  rendererElement: HTMLElement | null | undefined,
  aspectRatio: AspectRatio | string | undefined,
  options: RendererRectOptions = {},
): RendererRectState {
  const {
    dimContainerAspectRatio = 16 / 9,
    dynamicScaleFactor = true,
    offsets,
  } = options;

  // Stabilize offsets by value so callers passing inline `offsets={{...}}`
  // don't tear down the ResizeObserver on every render.
  const containerLeftOffset = offsets?.CONTAINER_LEFT_OFFSET ?? 0;
  const containerTopOffset = offsets?.CONTAINER_TOP_OFFSET ?? 0;
  const containerHeightOffset = offsets?.CONTAINER_HEIGHT_OFFSET ?? 0;
  const containerWidthOffset = offsets?.CONTAINER_WIDTH_OFFSET ?? 0;
  const overlayLeftOffset = offsets?.OVERLAY_LEFT_OFFSET ?? 0;
  const overlayTopOffset = offsets?.OVERLAY_TOP_OFFSET ?? 0;
  const stableOffsets = useMemo(
    () => ({
      containerLeftOffset,
      containerTopOffset,
      containerHeightOffset,
      containerWidthOffset,
      overlayLeftOffset,
      overlayTopOffset,
    }),
    [
      containerLeftOffset,
      containerTopOffset,
      containerHeightOffset,
      containerWidthOffset,
      overlayLeftOffset,
      overlayTopOffset,
    ],
  );

  const [rect, setRect] = useState<RendererRectState>(ZERO_RECT);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    if (!rendererElement) {
      setRect(ZERO_RECT);
      return;
    }

    const recompute = () => {
      const { width: parentWidth, height: parentHeight } =
        rendererElement.getBoundingClientRect();

      if (parentWidth <= 0 || parentHeight <= 0) {
        setRect(ZERO_RECT);
        return;
      }

      const arScale = dimContainerAspectRatio;
      const parentAspectRatio = parentWidth / parentHeight;
      const scaleFactor =
        parentAspectRatio === arScale && dynamicScaleFactor ? 1 : 0.8;

      let containerWidth: number;
      let containerHeight: number;
      if (parentAspectRatio <= arScale) {
        containerWidth = scaleFactor * parentWidth;
        containerHeight = containerWidth / arScale;
      } else {
        containerHeight = scaleFactor * parentHeight;
        containerWidth = containerHeight * arScale;
      }

      if (containerWidth > parentWidth) {
        containerWidth = parentWidth;
        containerHeight = containerWidth / arScale;
      }
      if (containerHeight > parentHeight) {
        containerHeight = parentHeight;
        containerWidth = containerHeight * arScale;
      }

      containerHeight += stableOffsets.containerHeightOffset;
      containerWidth += stableOffsets.containerWidthOffset;

      const containerLeft =
        Math.round((parentWidth - containerWidth) / 2) +
        stableOffsets.containerLeftOffset;
      const containerTop =
        Math.round((parentHeight - containerHeight) / 2) +
        stableOffsets.containerTopOffset;

      let overlayWidth = containerWidth;
      let overlayHeight = containerHeight;

      if (aspectRatio === AspectRatio.AR_1_1) {
        overlayWidth = overlayHeight = Math.min(containerWidth, containerHeight);
      } else if (aspectRatio === AspectRatio.AR_9_16) {
        overlayWidth = overlayHeight * (9 / 16);
      }

      const overlayLeft =
        Math.round((containerWidth - overlayWidth) / 2) +
        stableOffsets.overlayLeftOffset;
      const overlayTop =
        Math.round((containerHeight - overlayHeight) / 2) +
        stableOffsets.overlayTopOffset;

      setRect({
        containerLeft,
        containerTop,
        containerWidth,
        containerHeight,
        overlayLeft,
        overlayTop,
        overlayWidth,
        overlayHeight,
        parentWidth,
        parentHeight,
      });
    };

    recompute();

    resizeObserverRef.current = new ResizeObserver(() => recompute());
    resizeObserverRef.current.observe(rendererElement);

    return () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    };
  }, [
    rendererElement,
    aspectRatio,
    dimContainerAspectRatio,
    dynamicScaleFactor,
    stableOffsets,
  ]);

  return rect;
}
